import React from "react";
import { Link } from "react-router-dom";
import {
  FlaskConical,
  Timer,
  Music4,
  Waves,
  Star,
  Moon,
  Sparkle,
  ArrowUpRight,
  Hammer,
  Hash,
  Languages,
  Map,
  Brush,
  Piano,
  ListMusic,
  CircleDot,
  Palette,
} from "lucide-react";

// Big whimsical doodles drifting behind the content.
const DOODLES = [
  { Icon: FlaskConical, top: "14%", left: "9%", size: 70, opacity: 0.15, delay: "0s" },
  { Icon: Star, top: "22%", left: "84%", size: 48, opacity: 0.16, delay: "1.4s" },
  { Icon: Moon, top: "72%", left: "87%", size: 72, opacity: 0.13, delay: "0.7s" },
  { Icon: Sparkle, top: "80%", left: "8%", size: 44, opacity: 0.15, delay: "2.2s" },
];

const SECTIONS = [
  {
    id: "music",
    title: "Music & Sound",
    tagline: "Learning to hear like I mean it, one gadget at a time.",
    projects: [
      {
        to: "/number-sense",
        title: "Number Sense",
        blurb:
          "A gamified Nashville Number System trainer I'm building to learn music by ear.",
        Icon: Music4,
        tilt: "rotate-2 sm:-translate-y-1",
        wip: true,
      },
      {
        to: "/number-sense-v2",
        title: "Number Sense v2",
        blurb:
          "The scale-first remix: learn each scale type's recipe once, then drill it Duolingo-style in any key — crowns, streaks, and a speedrun timer you have to earn.",
        Icon: Hash,
        tilt: "-rotate-2 sm:translate-y-1",
        wip: true,
      },
      {
        to: "/synth-lab",
        title: "Synth Lab",
        blurb:
          "An ear-training playground for learning to patch a synth — match sounds, name the controls, and twist one knob at a time.",
        Icon: Waves,
        tilt: "-rotate-1 sm:translate-y-1",
        wip: true,
      },
      {
        to: "/scale-country",
        title: "Scale Country",
        blurb:
          "Scales are places. Wander a map of modes and odd pentatonics, noodle over a drone, and leave a melody on every page.",
        Icon: Map,
        tilt: "rotate-1 sm:-translate-y-1",
        wip: true,
      },
      {
        to: "/jianpu",
        title: "Jianpu Studio",
        blurb:
          "A songbook and composer for my 15-button EasyPlay, written in numbered jianpu notation — play along over Web MIDI for a score, or record a take and watch it become sheet music.",
        Icon: ListMusic,
        tilt: "rotate-2 sm:translate-y-1",
        wip: true,
      },
      {
        to: "/home-key",
        title: "Home Key",
        blurb:
          "An interactive course on why music works — melodies, chords, and grooves as trips away from one home note, with every lesson adding a layer to your own track.",
        Icon: Piano,
        tilt: "-rotate-1 sm:-translate-y-1",
        wip: true,
      },
      {
        to: "/piano-drills",
        title: "Piano Drills",
        blurb:
          "Build a chord progression, pick a rhythm, and drill it with your MIDI keyboard — guided mode plays it for you, or score mode grades your playing.",
        Icon: Piano,
        tilt: "rotate-2 sm:translate-y-1",
        wip: true,
      },
      {
        to: "/circle-of-fifths",
        title: "Circle of Fifths",
        blurb:
          "An interactive circle-of-fifths wheel — explore key signatures and their chords, drill triad inversions, and quiz yourself on finding keys.",
        Icon: Piano,
        tilt: "-rotate-2",
        wip: true,
      },
      {
        to: "/chord-craft",
        title: "Chord Craft",
        blurb:
          "A workshop for getting really good at chords — color them with extensions, voice them across two hands, connect them smoothly, and jam them over looped progressions from every genre.",
        Icon: Palette,
        tilt: "-rotate-1 sm:translate-y-1",
        wip: true,
      },
      {
        to: "/chord-caller",
        title: "Chord Caller",
        blurb:
          "The screen calls chords, you play them — flashcards for your piano's music stand.",
        Icon: Piano,
        tilt: "rotate-2 sm:-translate-y-1",
        wip: true,
      },
    ],
  },
  {
    id: "toki-pona",
    title: "toki pona corner",
    tagline: "A tiny language of good, in about 130 words.",
    projects: [
      {
        to: "/o-kama-sona",
        title: "o kama sona",
        blurb:
          "A little toki pona trainer — meet each new word, then drill it with lessons and practice games. The whole language of good in ~130 words.",
        Icon: Languages,
        tilt: "-rotate-2 sm:translate-y-1",
      },
      {
        to: "/ilo-sitelen",
        title: "ilo sitelen",
        blurb:
          "The toki pona sentence machine — type a sentence and watch it draw itself, or let the machine draw first and describe what you see.",
        Icon: Brush,
        tilt: "rotate-2 sm:translate-y-1",
        wip: true,
      },
      {
        to: "/sitelen-sike",
        title: "sitelen sike",
        blurb:
          "A round shorthand for toki pona — the shape is the consonant, the rotation is the vowel, and marks squeeze a whole word into one glyph. Drills included.",
        Icon: CircleDot,
        tilt: "rotate-1 sm:translate-y-1",
        wip: true,
      },
    ],
  },
  {
    id: "play",
    title: "Games & Making",
    tagline: "For the hands, not just the ears.",
    projects: [
      {
        to: "/timer",
        title: "Board Game Timer",
        blurb: "A simple timer to keep game nights moving along.",
        Icon: Timer,
        tilt: "-rotate-2 sm:translate-y-1",
      },
    ],
  },
];

const SillyProjects = () => {
  return (
    <div className="relative min-h-[calc(100vh-4rem)] overflow-hidden bg-dreamy">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-24 -top-24 h-96 w-96 rounded-full bg-primary/15 blur-3xl animate-aurora"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 -right-24 h-[28rem] w-[28rem] rounded-full bg-[hsl(var(--blue)/0.22)] blur-3xl animate-aurora"
        style={{ animationDelay: "-7s" }}
      />
      {DOODLES.map(({ Icon, top, left, size, opacity, delay }, i) => (
        <Icon
          key={i}
          aria-hidden
          className="pointer-events-none absolute text-primary animate-drift"
          style={{ top, left, width: size, height: size, opacity, animationDelay: delay }}
        />
      ))}

      <div className="container relative mx-auto max-w-3xl px-4 py-20">
        <p className="inline-flex items-center gap-1.5 text-sm font-medium text-primary">
          <FlaskConical className="h-4 w-4" />
          half-baked, fully shipped
        </p>
        <h1 className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
          <span className="text-gold-shimmer">Silly Projects</span>
        </h1>
        <p className="mt-5 max-w-xl text-lg text-muted-foreground">
          A little gallery of whatever my imagination dreams up — some polished,
          some happily still in progress.
        </p>

        {SECTIONS.map(({ id, title: sectionTitle, tagline, projects }) => (
          <div key={id} className="mt-12 sm:mt-14">
            <div className="border-t border-border/60 pt-6">
              <h2 className="text-xl font-semibold tracking-tight sm:text-2xl">
                {sectionTitle}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">{tagline}</p>
            </div>

            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              {projects.map(({ to, title, blurb, Icon, tilt, wip }) => (
                <Link
                  key={to}
                  to={to}
                  className={`group relative flex flex-col items-start gap-3 overflow-hidden rounded-2xl border border-border bg-card/70 p-5 text-left backdrop-blur transition-all duration-300 hover:rotate-0 hover:-translate-y-1.5 hover:border-primary/60 hover:glow-gold ${tilt}`}
                >
                  <div className="flex w-full items-center justify-between">
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground group-hover:animate-wiggle">
                      <Icon className="h-5 w-5" />
                    </span>
                    <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-primary" />
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="font-semibold text-card-foreground">{title}</h2>
                      {wip && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[0.65rem] font-medium uppercase tracking-wide text-primary">
                          <Hammer className="h-3 w-3" />
                          WIP · personal learning
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">{blurb}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SillyProjects;
